import { useState } from "react";
import { useTasks, type TaskOutput } from "../store/tasks";
import { CLIENTS, clientById } from "../data/clients";
import { PERSONAS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";

type Filter = "all" | "image" | "video";

interface GalleryItem extends TaskOutput {
  taskId: string;
  taskTitle: string;
  clientId?: string;
}

export function Gallery() {
  const tasks = useTasks(s => s.tasks);
  const [filter, setFilter] = useState<Filter>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [openItem, setOpenItem] = useState<GalleryItem | null>(null);

  // colhe todos os outputs que têm mídia
  const items: GalleryItem[] = tasks
    .flatMap(t => t.outputs.map(o => ({
      ...o, taskId: t.id, taskTitle: t.title, clientId: t.clientId
    })))
    .filter(o => o.kind === "image" || o.kind === "video")
    .filter(o => filter === "all" || o.kind === filter)
    .filter(o => clientFilter === "all" || o.clientId === clientFilter)
    .sort((a, b) => b.at - a.at);

  // agrupa por cliente
  const byClient = new Map<string, GalleryItem[]>();
  items.forEach(it => {
    const key = it.clientId ?? "_sem-cliente_";
    if (!byClient.has(key)) byClient.set(key, []);
    byClient.get(key)!.push(it);
  });

  const imgCount = items.filter(i => i.kind === "image").length;
  const vidCount = items.filter(i => i.kind === "video").length;

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-xs text-gold">GAL-01</div>
            <h2 className="font-display font-black uppercase text-4xl leading-none">GALERIA DE CRIATIVOS</h2>
            <div className="font-serif italic mt-1 text-bone/70">
              {items.length} peças · {imgCount} imagem · {vidCount} vídeo
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="flex border-2 border-bone">
              {(["all","image","video"] as Filter[]).map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`tag px-3 py-1.5 border-r last:border-r-0 border-bone
                    ${filter === f ? "bg-bone text-ink" : "text-bone hover:bg-bone/10"}`}>
                  {f === "all" ? "tudo" : f === "image" ? "imagem" : "vídeo"}
                </button>
              ))}
            </div>
            <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
              className="bg-bone text-ink px-2 py-1 font-mono text-[12px] border-2 border-bone">
              <option value="all">todos os clientes</option>
              {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              <option value="_sem-cliente_">— sem cliente —</option>
            </select>
          </div>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="border-2 border-graymd p-16 text-center font-serif italic text-paper">
          Nenhum criativo gerado ainda. Vai pro Quadro de Demandas → ✦ gerar.
        </div>
      ) : (
        <div className="space-y-6">
          {[...byClient.entries()].map(([cid, list]) => {
            const cli = cid === "_sem-cliente_" ? null : clientById(cid);
            return (
              <section key={cid} className="border-2 border-ink">
                <header className="px-4 py-2 flex items-center justify-between"
                        style={{ background: cli?.brand.primary ?? "#121214", color: cli?.brand.accent ?? "#F2EEE6" }}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs opacity-70">
                      {cli ? cli.id.toUpperCase() : "INTERNO"}
                    </span>
                    <span className="font-display font-black uppercase text-xl">
                      {cli ? cli.name : "sem cliente vinculado"}
                    </span>
                  </div>
                  <span className="tag">{list.length} peça{list.length === 1 ? "" : "s"}</span>
                </header>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 p-2 bg-bone">
                  {list.map(it => <GalleryCard key={it.id} item={it} onOpen={() => setOpenItem(it)} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {openItem && <Lightbox item={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
}

function GalleryCard({ item, onOpen }: { item: GalleryItem; onOpen: () => void }) {
  const p = PERSONAS.find(x => x.id === item.by);
  return (
    <button onClick={onOpen}
            className="block text-left border-2 border-ink/20 bg-bone hover:border-ink transition group">
      <div className="aspect-square bg-ink/5 flex items-center justify-center overflow-hidden">
        {item.kind === "image" && item.mediaUrl && (
          <img src={item.mediaUrl} alt="" className="w-full h-full object-cover" />
        )}
        {item.kind === "video" && item.mediaUrl && (
          <video src={item.mediaUrl} className="w-full h-full object-cover" muted />
        )}
      </div>
      <div className="p-2">
        <div className="flex items-center gap-1 mb-1">
          <span className={`tag px-1 ${item.kind === "image" ? "bg-gold text-ink" : "bg-red text-bone"}`}>
            {item.kind === "image" ? "IMG" : "VID"}
          </span>
          {p && (
            <span className="w-5 h-5 flex items-center justify-center font-display font-black text-[9px] text-bone"
                  style={{ background: monogramColor(p.id) }}>
              {initials(p.name)}
            </span>
          )}
          <span className="tag text-graydk truncate">{p?.name ?? item.by}</span>
        </div>
        <div className="font-mono text-[11px] truncate text-paper">{item.taskTitle}</div>
      </div>
    </button>
  );
}

function Lightbox({ item, onClose }: { item: GalleryItem; onClose: () => void }) {
  const p = PERSONAS.find(x => x.id === item.by);
  return (
    <div className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-bone max-w-4xl w-full max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-mono text-xs text-gold truncate">{item.taskTitle}</div>
            <div className="font-display font-black uppercase text-lg truncate">
              {p?.name ?? item.by} · {item.provider}/{item.model}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {item.mediaUrl && (
              <a href={item.mediaUrl}
                 download={`vpc-${item.id}.${item.kind === "image" ? "png" : "mp4"}`}
                 className="btn bg-bone text-ink border-bone">↓ baixar</a>
            )}
            <button onClick={onClose} className="btn bg-bone text-ink border-bone">×</button>
          </div>
        </header>
        <div className="bg-ink/10 flex items-center justify-center p-4">
          {item.kind === "image" && item.mediaUrl && (
            <img src={item.mediaUrl} alt="" className="max-w-full max-h-[70vh]" />
          )}
          {item.kind === "video" && item.mediaUrl && (
            <video src={item.mediaUrl} controls autoPlay className="max-w-full max-h-[70vh]" />
          )}
        </div>
        <pre className="p-4 font-mono text-[12px] whitespace-pre-wrap leading-relaxed bg-bone max-h-48 overflow-y-auto">
{item.text}
        </pre>
      </div>
    </div>
  );
}
